// What "cost" means when a group recharges salary between its companies
// (multi-entity §12.6).
//
// This is a tenant-wide accounting policy, not a per-person one, and it
// changes every number the recharge produces — so it is a deliberate setting
// rather than an assumption buried in code. It also does NOT re-price runs
// that are already calculated: their shares are frozen and their money derives
// from frozen calculations, so only future runs move.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/features/shared/components/ui/select';
import { getApiErrorMessage } from '@/lib/apiError';
import { getCostAllocationBasis, setCostAllocationBasis, type CostBasis } from '@/services/payrollSettingsService';

const OPTIONS: { value: CostBasis; label: string; hint: string }[] = [
  {
    value: 'employment_cost',
    label: 'Salary plus employer contributions',
    hint: 'NSSF employer, SDL and WCF ride along with the salary — what the employing company actually paid out.',
  },
  {
    value: 'salary_only',
    label: 'Salary only',
    hint: 'Gross pay alone. Employer statutory cost stays with the employing company as its own overhead.',
  },
];

/** Renders nothing for a single-company tenant: there is nothing to recharge. */
export const CostBasisSetting: React.FC<{ entityCount: number; canEdit: boolean }> = ({
  entityCount, canEdit,
}) => {
  const [basis, setBasis] = useState<CostBasis | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (entityCount < 2) return;
    getCostAllocationBasis().then((r) => setBasis(r.basis)).catch(() => setBasis(null));
  }, [entityCount]);

  if (entityCount < 2 || !basis) return null;

  const change = async (next: string) => {
    const prev = basis;
    setBasis(next as CostBasis);
    setSaving(true);
    try {
      await setCostAllocationBasis(next as CostBasis);
      toast.success('Cost basis saved. Runs already calculated keep the basis they were calculated with.');
    } catch (err) {
      setBasis(prev);
      toast.error(getApiErrorMessage(err, 'Could not save the cost basis.'));
    } finally {
      setSaving(false);
    }
  };

  const active = OPTIONS.find((o) => o.value === basis);

  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <h2 className="text-sm font-semibold text-foreground">Inter-company cost basis</h2>
        <p className="text-xs text-muted-foreground">
          When one company bears part of another’s salary cost, this is what the share is applied to.
          Recharges are always at cost — no margin is added.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={basis} onValueChange={(v) => void change(v)} disabled={!canEdit || saving}>
            <SelectTrigger className="h-9 w-[20rem]" aria-label="Inter-company cost basis">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />}
        </div>
        {active && <p className="text-xs text-muted-foreground">{active.hint}</p>}
      </CardContent>
    </Card>
  );
};

export default CostBasisSetting;
